import type { LLMConfig } from '../feature_flags/interface';
import { deserialize, fetchWrapper } from '../fetch';
import type {
  ClaimTemplate,
  ClaimTemplateListItem,
  TemplateSchema,
} from './interface';

export const CREATE_TEMPLATE_TIMEOUT = -1;

export const fetchTemplates = async (data: {
  dossier_id: string;
  template_type?: string;
}): Promise<{
  templates: ClaimTemplateListItem[];
  has_available_template: Record<string, boolean>;
  template_schemas: Record<string, TemplateSchema>;
}> => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossier_id}/templates`,
    API_BASE_URL
  );
  if (data.template_type) {
    url.searchParams.set('template_type', data.template_type);
  }
  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const fetchDetail = async (data: {
  dossier_id: string;
  template_id: string;
}): Promise<ClaimTemplate> => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossier_id}/templates/${data.template_id}`,
    API_BASE_URL
  );
  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const fetchTemplateState = async (data: {
  dossier_id: string;
  template_id: string;
}) => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossier_id}/templates/${data.template_id}/state`,
    API_BASE_URL
  );
  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const fetchTemplateSchemas = async (): Promise<string[]> => {
  const url = new URL(`/app/v1/templates/template_schemas`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const create = async (data: {
  dossier_id: string;
  template_type: string;
  llm_override?: LLMConfig;
}): Promise<void> => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossier_id}/templates`,
    API_BASE_URL
  );
  return deserialize(
    await fetchWrapper(
      url.href,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          template_type: data.template_type,
          llm_override: !data.llm_override ? undefined : [data.llm_override],
        }),
      },
      CREATE_TEMPLATE_TIMEOUT
    )
  );
};

export const downloadTemplateAsRtf = async (data: {
  dossierId: string;
  templateId: string;
  template_version_id?: string;
  includeCitations?: boolean;
}): Promise<{ rtf_content: string }> => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossierId}/templates/${data.templateId}/download_as_rtf`,
    API_BASE_URL
  );
  if (data.template_version_id) {
    url.searchParams.set('template_version_id', data.template_version_id);
  }
  if (data.includeCitations !== undefined) {
    url.searchParams.set('include_citations', String(data.includeCitations));
  }
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  );
};

export const restoreTemplateVersion = async (data: {
  dossier_id: string;
  template_id: string;
  version_id: string;
}): Promise<{
  template_version: {
    html: string;
    template_version_id: string;
    created_at: string;
  };
}> => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossier_id}/templates/${data.template_id}/versions/${data.version_id}/restore`,
    API_BASE_URL
  );
  return deserialize(
    await fetchWrapper(url.href, { method: 'POST', credentials: 'include' })
  );
};
